/**
 * Audience scope of a notification: tells "as whom" the current user received it.
 * Mirrors the backend NotificationScopeType (user | circle | network).
 *
 * The scope drives two things on the client:
 * - the deterministic context switch when a notification is clicked
 * - grouping/badging notifications by context in the list
 */
export type NotificationScopeType = 'user' | 'circle' | 'network'

export interface NotificationScopeInfo {
  scopeType?: NotificationScopeType | string | null
  scopeIri?: string | null
  scopeName?: string | null
}

/**
 * Whether the notification carries an admin context (circle/network with an IRI). User-scoped
 * (individual) notifications return false — they get no contextual badge and no context switch.
 */
export const isContextualScope = (
  scope: NotificationScopeInfo | null | undefined
): scope is NotificationScopeInfo & { scopeIri: string } =>
  !!scope &&
  (scope.scopeType === 'circle' || scope.scopeType === 'network') &&
  !!scope.scopeIri

/**
 * Target context IRI for a notification scope: the circle/network IRI for an admin scope,
 * or null for an individual (user) scope. null means "the user's own context".
 */
export function resolveTargetContextIri(
  scope: NotificationScopeInfo | null | undefined
): string | null {
  return isContextualScope(scope) ? scope.scopeIri : null
}

/**
 * Whether clicking the notification should switch context, comparing its target context
 * against the currently active one (null = user context).
 */
export function scopeRequiresContextSwitch(
  scope: NotificationScopeInfo | null | undefined,
  currentContextIri: string | null
): boolean {
  return resolveTargetContextIri(scope) !== (currentContextIri ?? null)
}
